/**
 * Render-time article HTML enhancements (pure, runs during SSR so links are in server HTML):
 * glossary auto-links on first mention, and ids + a contents list for posts with 5+ H2s.
 */

export const GLOSSARY_LINK_TERMS: { slug: string; names: string[] }[] = [
  { slug: "ad-rank", names: ["Ad Rank"] },
  { slug: "attribution-window", names: ["attribution window"] },
  { slug: "call-tracking", names: ["call tracking"] },
  { slug: "conversion-action", names: ["conversion action"] },
  { slug: "conversion-rate", names: ["conversion rate", "CVR"] },
  { slug: "conversion-tracking", names: ["conversion tracking"] },
  { slug: "conversions-api", names: ["Conversions API", "CAPI"] },
  { slug: "cost-per-acquisition", names: ["cost per acquisition", "CPA"] },
  { slug: "cost-per-click", names: ["cost per click", "CPC"] },
  { slug: "cost-per-lead", names: ["cost per lead", "CPL"] },
  { slug: "google-analytics-4", names: ["Google Analytics 4", "GA4"] },
  { slug: "google-tag-manager", names: ["Google Tag Manager", "GTM"] },
  { slug: "impression-share", names: ["impression share"] },
  { slug: "local-services-ads", names: ["Local Services Ads", "LSA"] },
  { slug: "match-types", names: ["match types"] },
  { slug: "meta-pixel", names: ["Meta pixel"] },
  { slug: "negative-keywords", names: ["negative keywords"] },
  { slug: "primary-conversion", names: ["primary conversion"] },
  { slug: "quality-score", names: ["Quality Score"] },
  { slug: "return-on-ad-spend", names: ["return on ad spend", "ROAS"] },
  { slug: "search-terms-report", names: ["search terms report"] },
  { slug: "server-side-tagging", names: ["server-side tagging"] },
];

export const MAX_GLOSSARY_LINKS = 5;
const SKIP_TAGS = new Set(["a", "h1", "h2", "h3", "h4", "h5", "h6", "figcaption", "script", "style", "code", "pre", "button", "summary", "details", "aside"]);
const FAQ_HEADING = /\b(faq|frequently asked)\b/i;

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const WORD = "A-Za-z0-9";

export function autolinkGlossary(html: string): { html: string; count: number } {
  const remaining = GLOSSARY_LINK_TERMS.filter((t) => !new RegExp(`href=["'](?:https?://[^"']*)?/glossary/${esc(t.slug)}(?:[/?#"'])`, "i").test(html));
  const patterns = remaining.map((t) => ({ slug: t.slug, re: new RegExp(`(?<![${WORD}-])(?:${t.names.map(esc).join("|")})(?![${WORD}-])`, "i") }));
  const used = new Set<string>();
  let count = 0;
  const skip: string[] = [];
  let faq = false;
  let pendingH2 = false;
  let h2Text = "";
  const out: string[] = [];
  const tokens = html.split(/(<[^>]+>)/);
  for (const tok of tokens) {
    if (tok.startsWith("<")) {
      const m = /^<(\/)?([a-zA-Z0-9]+)/.exec(tok);
      if (m) {
        const tag = m[2]!.toLowerCase();
        const closing = Boolean(m[1]);
        const selfClosing = tok.endsWith("/>");
        if (tag === "h2" && !closing) { pendingH2 = true; h2Text = ""; }
        if (tag === "h2" && closing) { pendingH2 = false; faq = FAQ_HEADING.test(h2Text); }
        if (SKIP_TAGS.has(tag) && !selfClosing) {
          if (closing) { const i = skip.lastIndexOf(tag); if (i >= 0) skip.splice(i, 1); }
          else skip.push(tag);
        }
      }
      out.push(tok);
      continue;
    }
    if (pendingH2) h2Text += tok;
    if (skip.length || faq || count >= MAX_GLOSSARY_LINKS || !tok.trim()) { out.push(tok); continue; }
    let rest = tok;
    let built = "";
    while (count < MAX_GLOSSARY_LINKS) {
      let best: { slug: string; index: number; text: string } | null = null;
      for (const p of patterns) {
        if (used.has(p.slug)) continue;
        const hit = p.re.exec(rest);
        if (hit && (!best || hit.index < best.index)) best = { slug: p.slug, index: hit.index, text: hit[0] };
      }
      if (!best) break;
      used.add(best.slug);
      count++;
      built += `${rest.slice(0, best.index)}<a href="/glossary/${best.slug}">${best.text}</a>`;
      rest = rest.slice(best.index + best.text.length);
    }
    out.push(built + rest);
  }
  return { html: out.join(""), count };
}

export interface TocItem { id: string; text: string }

export function slugifyHeading(text: string): string {
  return text.toLowerCase().replace(/&[a-z#0-9]+;/g, "").replace(/<[^>]+>/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "section";
}

const decode = (s: string) => s.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&#39;|&rsquo;/g, "’").replace(/&quot;/g, '"').replace(/&nbsp;/g, " ").trim();

/** Adds ids to H2s (keeping any existing id) and returns them; heading text is untouched. */
export function addHeadingIds(html: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = [];
  const seen = new Set<string>();
  const result = html.replace(/<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi, (whole, attrs: string | undefined, inner: string) => {
    const existing = /\sid=["']([^"']+)["']/.exec(attrs ?? "");
    let id = existing?.[1] ?? slugifyHeading(inner);
    if (!existing) { let n = 2; const base = id; while (seen.has(id)) id = `${base}-${n++}`; }
    seen.add(id);
    toc.push({ id, text: decode(inner) });
    return existing ? whole : `<h2 id="${id}"${attrs ?? ""}>${inner}</h2>`;
  });
  return { html: result, toc };
}

export function enhanceArticle(html: string): { html: string; toc: TocItem[]; glossaryLinks: number } {
  const linked = autolinkGlossary(html);
  const ids = addHeadingIds(linked.html);
  return { html: ids.html, toc: ids.toc.length >= 5 ? ids.toc : [], glossaryLinks: linked.count };
}
